import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { PimoComponent } from "@pimo/pimo-app-builder";

import PMBaseCard from "../pm-base-card/pm-base-card";

export interface PMLineGaugeCardState {
  startDate?: string;
  showStartDate?: boolean;
  endDate?: string;
  showEndDate?: boolean;
  title: string;
  icon?: string;
  showInfoIcon?: boolean;
  showMenuIcon?: boolean;
  fixedWidth?: number | string;
  fixedHeight?: number | string;
  entries: {
    label: string;
    percentage: number;
    precision?: number;
    color?: string;
    value: number;
    isTitle?: boolean;
  }[];
  scale: number[];
}

export const PMLineGaugeCard: PimoComponent<PMLineGaugeCardState> = ({
  entries = [],
  scale = [0, 100],
  title,
  icon = "TrendingUp",
  showInfoIcon = false,
  showMenuIcon = false,
  fixedWidth = "100%",
}) => {
  const theme = useTheme();
  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={"100%"}
    >
      <Box sx={{ p: 3, pr: 6 }}>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Box
            sx={{
              flexBasis: "25%",
              fontSize: "12px",
              "@media print": { flexBasis: "50%" },
            }}
          ></Box>
          <Box
            sx={{
              flexBasis: "75%",
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
              fontSize: "12px",
              fontWeight: "400",
              mb: 2,
              "@media print": { flexBasis: "50%" },
            }}
          >
            {scale.map((scaleEntry) => (
              <Typography key={`scale_${scaleEntry}`} sx={{ fontSize: "12px" }}>
                {scaleEntry}
              </Typography>
            ))}
          </Box>
        </Box>
        {entries.map((entry) => (
          <Box
            key={entry.label}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                flexBasis: "25%",
                fontSize: "12px",
                fontWeight: entry.isTitle ? 700 : 400,
                "@media print": { flexBasis: "50%" },
              }}
            >
              {entry.label}
            </Typography>
            <Box
              sx={{ flexBasis: "75%", "@media print": { flexBasis: "50%" } }}
            >
              <Box sx={{ background: "#fff", py: "12px" }}>
                {/* container around line and indicator */}
                <Box
                  sx={{
                    position: "relative",
                    height: "16px",
                    display: "block",
                    marginTop: "10px",
                  }}
                >
                  {/* line */}
                  <Box
                    sx={{
                      display: "block",
                      width: "100%",
                      height: "3px",
                      borderRadius: "30px",
                      background: "#DFEFF2",
                    }}
                  />
                  {/* indicator */}
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "common.white",
                          color: "black",
                          border: "1px solid",
                          borderColor: theme.palette.secondary.main,
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 400,
                          p: 1,
                        },
                      },
                    }}
                    title={entry.value?.toPrecision(entry.precision)}
                  >
                    <Box
                      data-testid="pm-line-gauge-dot"
                      sx={{
                        width: "20px",
                        height: "20px",
                        background: entry.color ?? theme.palette.primary.main,
                        position: "relative",
                        top: "-11px",
                        borderRadius: "100%",
                        cursor: "pointer",
                        // makes sure that when percentage = 0, the indicator is on the left, and
                        // when percentage = 100, it is -20px off the right, so its right side aligns with the
                        // right side of the line
                        left: `calc(${entry.percentage}% - ${(entry.percentage / 100) * 20}px)`,
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      {/* Spacer box to push content below to the bottom */}
      <Box sx={{ flexShrink: 1, flexGrow: 1, flexBasis: 0 }} />
    </PMBaseCard>
  );
};

export default PMLineGaugeCard;
